/**
 * Pestaña de Trazabilidad - Lista de prospectos con su ciclo de vida
 */

import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  Button,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search,
  Visibility,
  Timeline,
  Person,
  AccessTime,
  Badge,
} from '@mui/icons-material';

import {
  useFetchTrazabilidadProspectos,
  formatFechaAuditoria,
} from '@/shared/actions/erp/auditoria-ventas-home.actions';
import type { AuditoriaFiltros, AuditoriaVentasHome } from '@/shared/interfaces/erp/auditoria-ventas-home.interface';
import { TrazabilidadTimelineDialog } from './TrazabilidadTimelineDialog';

export const TrazabilidadTab = () => {
  const theme = useTheme();

  const [filtros, setFiltros] = useState<AuditoriaFiltros>({ page: 1, page_size: 25 });
  const [searchText, setSearchText] = useState('');
  const [selectedProspectoId, setSelectedProspectoId] = useState<string | null>(null);
  const [selectedProspectoCode, setSelectedProspectoCode] = useState<string | undefined>(undefined);
  const [timelineOpen, setTimelineOpen] = useState(false);

  const { data, isLoading } = useFetchTrazabilidadProspectos(filtros);

  const handleSearch = () => {
    setFiltros({ ...filtros, search: searchText || undefined, page: 1 });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setFiltros({ ...filtros, page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiltros({ ...filtros, page_size: parseInt(event.target.value, 10), page: 1 });
  };

  const handleViewTimeline = (log: AuditoriaVentasHome) => {
    setSelectedProspectoId(log.registro_id);
    setSelectedProspectoCode(log.registro_codigo || log.registro_id?.substring(0, 8) || undefined);
    setTimelineOpen(true);
  };

  return (
    <>
      {/* Barra de búsqueda */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          borderTop: `3px solid ${theme.palette.primary.main}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar por código, usuario, descripción..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ flex: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={handleSearch}
            startIcon={<Search />}
          >
            Buscar
          </Button>
        </Stack>
      </Paper>

      {/* Tabla de prospectos */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  '& .MuiTableCell-head': {
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'text.secondary',
                    py: 1.5,
                  },
                }}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <AccessTime sx={{ fontSize: 16 }} /> Fecha Creación
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Badge sx={{ fontSize: 16 }} /> Código Prospecto
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Person sx={{ fontSize: 16 }} /> Usuario
                  </Box>
                </TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                    <Timeline sx={{ fontSize: 16 }} /> Trazabilidad
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Cargando prospectos...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : data?.results?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Timeline sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No se encontraron prospectos
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data?.results?.map((log) => (
                  <TableRow
                    key={log.id_bitacora}
                    hover
                    onClick={() => handleViewTimeline(log)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                      {log.fecha_hora_local || formatFechaAuditoria(log.fecha_hora)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.registro_codigo || log.registro_id?.substring(0, 8) || '-'}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: 'monospace' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                        {log.usuario_nombre || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                        {log.descripcion || 'Creación de prospecto'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver trazabilidad completa">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTimeline(log);
                          }}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={data?.count || 0}
          page={(filtros.page || 1) - 1}
          onPageChange={handleChangePage}
          rowsPerPage={filtros.page_size || 25}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
        />
      </Paper>

      {/* Dialog de timeline */}
      <TrazabilidadTimelineDialog
        prospectoId={selectedProspectoId || ''}
        prospectoCode={selectedProspectoCode}
        open={timelineOpen}
        onClose={() => {
          setTimelineOpen(false);
          setSelectedProspectoId(null);
          setSelectedProspectoCode(undefined);
        }}
      />
    </>
  );
};
